import { useQuery, useMutation, useQueryClient } from 'react-query';
import * as invoiceService from '@/services/invoiceService';
import type {
  InvoiceFilter,
  CreateInvoiceRequest,
  UpdateInvoiceRequest,
  InvoiceStatus,
} from '@/types/invoice';

// ============================================================================
// Query Keys
// ============================================================================

export const INVOICE_QUERY_KEYS = {
  all: ['invoices'] as const,
  lists: () => [...INVOICE_QUERY_KEYS.all, 'list'] as const,
  list: (filter?: InvoiceFilter) => [...INVOICE_QUERY_KEYS.lists(), filter] as const,
  details: () => [...INVOICE_QUERY_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...INVOICE_QUERY_KEYS.details(), id] as const,
  nextNumber: () => ['invoice', 'nextNumber'] as const,
};

// ============================================================================
// Invoice Hooks
// ============================================================================

export function useInvoices(filter?: InvoiceFilter) {
  return useQuery(
    INVOICE_QUERY_KEYS.list(filter),
    () => invoiceService.listInvoices(filter),
    {
      keepPreviousData: true,
      staleTime: 30000,
      cacheTime: 300000,
    }
  );
}

export function useInvoice(id: string | undefined) {
  return useQuery(
    INVOICE_QUERY_KEYS.detail(id!),
    () => invoiceService.getInvoice(id!),
    {
      enabled: !!id,
      staleTime: 30000,
    }
  );
}

export function useCreateInvoice() {
  const queryClient = useQueryClient();

  return useMutation(
    (request: CreateInvoiceRequest) => invoiceService.createInvoice(request),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(INVOICE_QUERY_KEYS.lists());
        queryClient.invalidateQueries(INVOICE_QUERY_KEYS.nextNumber());
      },
    }
  );
}

export function useUpdateInvoice() {
  const queryClient = useQueryClient();

  return useMutation(
    ({ id, request }: { id: string; request: UpdateInvoiceRequest }) =>
      invoiceService.updateInvoice(id, request),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(INVOICE_QUERY_KEYS.lists());
        queryClient.setQueryData(INVOICE_QUERY_KEYS.detail(variables.id), data);
      },
    }
  );
}

export function useUpdateInvoiceStatus() {
  const queryClient = useQueryClient();

  return useMutation(
    ({ id, status, paidDate }: { id: string; status: InvoiceStatus; paidDate?: string }) =>
      invoiceService.updateInvoiceStatus(id, status, paidDate),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(INVOICE_QUERY_KEYS.lists());
        queryClient.invalidateQueries(INVOICE_QUERY_KEYS.detail(variables.id));
      },
    }
  );
}

export function useDeleteInvoice() {
  const queryClient = useQueryClient();

  return useMutation(
    (id: string) => invoiceService.deleteInvoice(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(INVOICE_QUERY_KEYS.lists());
      },
    }
  );
}

export function useNextInvoiceNumber() {
  return useQuery(
    INVOICE_QUERY_KEYS.nextNumber(),
    () => invoiceService.getNextInvoiceNumber(),
    {
      staleTime: 5000,
    }
  );
}
